/** Shared UTC date/bound parsing for schemas, conditions and submission rules. */
const DAY = 86_400_000
const RELATIVE = /^today(?:([+-])(\d{1,5})d)?$/i
const CALENDAR = /^\d{4}-\d{2}-\d{2}$/
const TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2})?$/i
export interface ResolvedDateBound { ms: number; dayOnly: boolean }

/** UTC calendar arithmetic, independent of browser/server locale and DST. */
export function parseUtcDate(value: unknown, now = new Date(), relative = true): ResolvedDateBound | null {
  if (typeof value !== 'string') return null
  const text = value.trim()
  const match = relative ? RELATIVE.exec(text) : null
  if (match) {
    const offset = Number(match[2] ?? 0) * (match[1] === '-' ? -1 : 1)
    if (Math.abs(offset) > 36500) return null
    return { ms: Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) + offset * DAY, dayOnly: true }
  }
  const timestamp = TIMESTAMP.exec(text)
  const calendar = CALENDAR.test(text) ? text : timestamp?.[1]
  if (!calendar || calendar.startsWith('0000')) return null
  const day = Date.parse(`${calendar}T00:00:00Z`)
  if (!Number.isFinite(day) || new Date(day).toISOString().slice(0, 10) !== calendar) return null
  if (!timestamp) return { ms: day, dayOnly: true }
  if (Number(timestamp[2]) > 23 || Number(timestamp[3]) > 59 || Number(timestamp[4] ?? 0) > 59) return null
  const zone = timestamp[6]
  if (zone && zone.toUpperCase() !== 'Z' && Number(zone.slice(1, 3)) > 15) return null
  const ms = Date.parse(text.replace(' ', 'T') + (zone ? '' : 'Z'))
  return Number.isFinite(ms) ? { ms, dayOnly: false } : null
}
export function compareUtcDates(a: unknown, b: unknown, now = new Date()): number | null {
  const left = parseUtcDate(a, now), right = parseUtcDate(b, now)
  if (!left || !right) return null
  return left.dayOnly || right.dayOnly ? Math.floor(left.ms / DAY) - Math.floor(right.ms / DAY) : left.ms - right.ms
}
